import { makeObservable, observable, action, runInAction } from "mobx";
import { DocumentSnapshot, QuerySnapshot } from "firebase/firestore";
import { RootStore } from "./rootStore";

// Define a base interface to ensure all models have an ID
interface Identifiable {
    id?: string;
    parentId?: string;
    actionDate?: any; 
}

export abstract class BaseStore<T extends Identifiable> {
    items: T[] = [];
    rootStore?: RootStore;
    selectedItem: T | null = null;
    isLoaded: boolean = false;

    constructor(rootStore?: RootStore) {
        this.rootStore = rootStore;
         makeObservable(this, {
            items: observable,
            selectedItem: observable,
            isLoaded: observable,
            loadAll: action,
            register: action,
            manage: action,
            //delete: action,
            setItem: action
        });
    }

    // Abstract getter to be implemented by child (e.g., return firebaseAgent.productService)
    protected abstract getService(): any;

    loadAll = async (parentId?: string, fieldName: string = "parentId") => {
        try {
            // Switch between filtered query and full collection based on parentId
            const snapshot: QuerySnapshot = parentId
                ? await this.getService().getByParent(parentId, fieldName)
                : await this.getService().getAll();

            runInAction(() => {
                this.items = snapshot.docs.map(doc => {
                    const data = doc.data() as T;
                    data.id = doc.id;
                    return data;
                });
                this.isLoaded = true;
            });
        } catch (error) {
            console.error("Error loading items:", error);
            throw error;
        }
    };

    register = async (data: T) => {
        try {
            const docRef = await this.getService().register(data);
            const snapshot = await this.getService().manage(docRef.id, {}); // just to fetch back
            const newItem = { ...(snapshot.data() as T), id: snapshot.id };
            this.setItem(newItem);
            return newItem;
        } catch (error) {
            throw error;
        }
    };

    manage = async (id: string, data: T) => {
        try {
            const snapshot = await this.getService().manage(id, data);
            const updatedItem = { ...(snapshot.data() as T), id: snapshot.id };
            this.setItem(updatedItem);
        } catch (error) {
            throw error;
        }
    };

    delete = async (id: string) => {
        try {
            await this.getService().delete(id);
            runInAction(() => {
                this.items = this.items.filter(item => item.id !== id);
            });
        } catch (error) {
            throw error;
        }
    };

    setItem = (item: T) => {
        runInAction(() => {
            this.selectedItem = item;
            const index = this.items.findIndex(i => i.id === item.id);
            if (index !== -1) {
                this.items[index] = item;
            } else {
                this.items.push(item);
            }
        });
    };

    getItem = async (id: string) => {
        const item = await this.items.find(i => i.id === id);
        if (item) this.selectedItem = item;
        return item;
    };

    getParentItem = (id: string) => {
        const item = this.items.find(i => i.parentId === id);
        if (item) this.selectedItem = item;
        return item;
    };
    // --- State Setters ---
    setItemload = (state: boolean) => {
        runInAction(() => { this.isLoaded = state; });
    }
}